import { Injectable } from '@angular/core';
import { ChartAxisUtil } from '../../shared/utils/chart-axis.util';

export interface ChartData {
  labels: string[];
  data: { values: (number | null)[]; label: string; type: string };
}

export interface ChartPoint {
  ingestionMonth: string;
  value: string;
  label?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChartConfigService {
  /**
   * Generates complete chart data, filling in all months of the selected range.
   * Missing months in the results will be filled with null or the last known value.
   *
   * @param results Values returned from the service (may be missing months)
   * @param indicatorName Name of the indicator (used as dataset label)
   * @param monthsRange Array of objects { date: Date, label: string } for all months in the range
   * @returns ChartData object with normalized labels and values
   */
  processChartData(
    results: ChartPoint[],
    indicatorName: string = 'Valor',
    monthsRange: { date: Date; label: string }[] = [],
  ): ChartData {
    const resultMap = new Map<string, number>();
    results.forEach((r) => {
      const d = new Date(r.ingestionMonth);
      const key = `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
      if (r.value != null && r.value !== '') {
        resultMap.set(key, parseFloat(r.value));
      }
    });

    const labels: string[] = [];
    const values: (number | null)[] = [];
    let lastValue: number | null = null;
    let foundFirstValue = false;

    for (const { date, label } of monthsRange) {
      const key = `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1)
        .toString()
        .padStart(2, '0')}`;
      labels.push(label);
      if (resultMap.has(key)) {
        const v = resultMap.get(key) ?? null;
        values.push(v);
        lastValue = v;
        foundFirstValue = true;
      } else if (!foundFirstValue) {
        values.push(null);
      } else {
        values.push(lastValue);
      }
    }

    return {
      labels,
      data: { values, label: indicatorName, type: 'line' },
    };
  }

  /**
   * Generates chart options for Chart.js based on the provided data and optional title.
   * Calculates Y axis range and tick steps using ChartAxisUtil.
   *
   * @param data Array of numbers to be plotted
   * @param title Optional chart title
   * @returns Chart.js options object
   */
  generateChartOptions(data: number[], title?: string): any {
    const yAxisRange = ChartAxisUtil.calculateYAxisRange(data, title);

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: !!title,
          text: title,
        },
        legend: {
          display: true,
          position: 'top',
        },
      },
      scales: {
        x: {
          display: true,
        },
        y: {
          display: true,
          min: yAxisRange.min,
          max: yAxisRange.max,
          ticks: {
            stepSize: yAxisRange.step,
            callback: function (value: any) {
              return yAxisRange.ticks.includes(value) ? value : '';
            },
          },
        },
      },
    };
  }

  validateChartData(results: ChartPoint[]): {
    isValid: boolean;
    error?: string;
  } {
    if (!results || !Array.isArray(results)) {
      return { isValid: false, error: 'Chart data must be an array' };
    }

    if (results.length === 0) {
      return { isValid: false, error: 'Chart data cannot be empty' };
    }

    const invalidItems = results.filter(
      (item) =>
        !item.ingestionMonth ||
        isNaN(new Date(item.ingestionMonth).getTime()) ||
        !item.value ||
        isNaN(parseFloat(item.value)),
    );

    if (invalidItems.length > 0) {
      return {
        isValid: false,
        error: `Found ${invalidItems.length} invalid data points`,
      };
    }

    return { isValid: true };
  }

  createEmptyChartData(): ChartData {
    return { labels: [], data: { values: [], label: '', type: '' } };
  }
}
